import React, { useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, TextField, Typography
} from '@mui/material';
import { EventAvailable } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { Session } from '../../types/sessions';
import { sessionsApi } from '../../services/sessions/sessionsApi';

interface CreateTargetSessionDialogProps {
  open: boolean;
  sourceSession: Session | null;
  onClose: () => void;
  onCreated: (created: Session) => void;
}

interface FormValues {
  name: string;
  startDate: Date | null;
  endDate: Date | null;
}

const schema = yup.object({
  name: yup.string().trim().required('Name is required').max(80),
  startDate: yup.date().nullable().required('Pick a start date').typeError('Invalid date'),
  endDate: yup.date().nullable().required('Pick an end date').typeError('Invalid date')
    .min(yup.ref('startDate'), 'End date must be on or after start date')
});

// Stable reference so Formik doesn't reinitialize on every render.
const INITIAL_VALUES: FormValues = { name: '', startDate: null, endDate: null };

const CreateTargetSessionDialog: React.FC<CreateTargetSessionDialogProps> = ({
  open, sourceSession, onClose, onCreated
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik<FormValues>({
    initialValues: INITIAL_VALUES,
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const created = await sessionsApi.create({
          name: values.name.trim(),
          startDate: values.startDate!.toISOString(),
          endDate: values.endDate!.toISOString()
        });
        enqueueSnackbar('Target session created', { variant: 'success' });
        onCreated(created);
        onClose();
      } catch (err: any) {
        enqueueSnackbar(err?.message || 'Could not create session', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  // Reset to a blank form every time the dialog reopens.
  useEffect(() => {
    if (open) formik.resetForm({ values: INITIAL_VALUES });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={formik.isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
        <form onSubmit={formik.handleSubmit} noValidate>
          <DialogTitle component="div" sx={{ pb: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <EventAvailable color="primary" fontSize="small" />
              <Typography variant="h6" fontWeight={600}>Create the target session</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              The new session is created in <strong>planning</strong> status. You can activate it later when the school year actually begins.
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2.5}>
              <TextField
                name="name"
                label="Session name"
                placeholder="e.g. 2025-2026"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                autoFocus
                fullWidth
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <DatePicker
                  label="Start date"
                  value={formik.values.startDate}
                  onChange={(date) => formik.setFieldValue('startDate', date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label="End date"
                  value={formik.values.endDate}
                  onChange={(date) => formik.setFieldValue('endDate', date)}
                  minDate={formik.values.startDate ?? undefined}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Stack>
              {formik.values.startDate && formik.values.endDate && (
                <Typography variant="caption" color="text.secondary">
                  {format(formik.values.startDate, 'MMM d, yyyy')} → {format(formik.values.endDate, 'MMM d, yyyy')}
                </Typography>
              )}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={onClose} disabled={formik.isSubmitting}>Cancel</Button>
            <Button type="submit" variant="contained" disableElevation disabled={formik.isSubmitting}>
              {formik.isSubmitting ? 'Creating…' : 'Create session'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateTargetSessionDialog;
